import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { useMemo, type ReactNode } from 'react';

import { Menu, type MenuItem } from '@/components/ui/Menu';
import { Tooltip } from '@/components/ui/Tooltip';
import { cn } from '@/lib/cn';

/**
 * Pagination — two modes for the two shapes of list we ship.
 *
 * `cursor` (default) matches the keyset API: previous / next only, with an honest
 * "1–50 of 912" range instead of a page count the server cannot cheaply produce.
 * `pages` renders numbered pages with a collapsed middle for anything that does know
 * its page count.
 *
 * Numbers are `.tnum` so the row does not twitch as you page through.
 */

export type PaginationMode = 'cursor' | 'pages';

export interface PaginationProps {
  mode?: PaginationMode;

  /** `pages` mode — 1-based. */
  page?: number;
  pageCount?: number;
  onPageChange?: (page: number) => void;

  /** `cursor` mode. */
  hasPrev?: boolean;
  hasNext?: boolean;
  onPrev?: () => void;
  onNext?: () => void;

  /** Shared. */
  total?: number;
  /** Rows on the current page — drives the "1–50 of 912" range. */
  pageSize?: number;
  pageSizeOptions?: number[];
  onPageSizeChange?: (size: number) => void;
  /** Override the derived range copy entirely. */
  rangeLabel?: ReactNode;
  /** Plural noun for the range copy. */
  entity?: string;
  loading?: boolean;
  disabled?: boolean;
  compact?: boolean;
  className?: string;
}

export function Pagination({
  mode = 'cursor',
  page = 1,
  pageCount,
  onPageChange,
  hasPrev,
  hasNext,
  onPrev,
  onNext,
  total,
  pageSize,
  pageSizeOptions,
  onPageSizeChange,
  rangeLabel,
  entity = 'results',
  loading = false,
  disabled = false,
  compact = false,
  className,
}: PaginationProps) {
  const pages = useMemo(
    () => (mode === 'pages' && pageCount ? buildPageList(page, pageCount) : []),
    [mode, page, pageCount],
  );

  const derivedRange = useMemo(() => {
    if (rangeLabel !== undefined) return rangeLabel;
    if (total === undefined) return null;
    if (total === 0) return `No ${entity}`;
    // Keyset paging has no page index, so only `pages` mode can state a range.
    if (!pageSize || mode !== 'pages') {
      return (
        <>
          <span className="tnum text-secondary">{formatNumber(total)}</span> {entity}
        </>
      );
    }
    const from = (page - 1) * pageSize + 1;
    const to = Math.min(page * pageSize, total);
    return (
      <>
        <span className="tnum text-secondary">
          {formatNumber(from)}–{formatNumber(to)}
        </span>{' '}
        of <span className="tnum text-secondary">{formatNumber(total)}</span> {entity}
      </>
    );
  }, [entity, mode, page, pageSize, rangeLabel, total]);

  const canPrev = (mode === 'pages' ? page > 1 : Boolean(hasPrev)) && !disabled;
  const canNext =
    (mode === 'pages' ? Boolean(pageCount) && page < (pageCount ?? 1) : Boolean(hasNext)) &&
    !disabled;

  const sizeItems: MenuItem[] = (pageSizeOptions ?? []).map((size) => ({
    id: String(size),
    type: 'radio' as const,
    label: `${size} per page`,
    checked: size === pageSize,
    onSelect: () => onPageSizeChange?.(size),
  }));

  return (
    <nav
      aria-label="Pagination"
      className={cn(
        'flex items-center gap-3 border-t border-line bg-[var(--surface-1)] px-4',
        compact ? 'h-[var(--h-row-compact)]' : 'h-[var(--h-topbar)]',
        className,
      )}
    >
      <p
        aria-live="polite"
        className={cn(
          'min-w-0 flex-1 truncate text-sm text-muted transition-opacity ease-[var(--ease-out)] duration-[var(--dur-fast)]',
          loading && 'opacity-50',
        )}
      >
        {derivedRange}
      </p>

      {sizeItems.length > 0 ? (
        <Menu
          items={sizeItems}
          align="end"
          side="top"
          label="Rows per page"
          trigger={
            <button type="button" disabled={disabled} className={pageSizeTriggerClass}>
              <span className="tnum">{pageSize}</span>
              <span className="text-muted">per page</span>
            </button>
          }
        />
      ) : null}

      <div className="flex shrink-0 items-center gap-1">
        {mode === 'pages' ? (
          <NavButton
            label="First page"
            icon={ChevronsLeft}
            disabled={!canPrev}
            onClick={() => onPageChange?.(1)}
          />
        ) : null}

        <NavButton
          label="Previous page"
          icon={ChevronLeft}
          disabled={!canPrev}
          onClick={() => (mode === 'pages' ? onPageChange?.(page - 1) : onPrev?.())}
        />

        {mode === 'pages' ? (
          <ol className="flex items-center gap-0.5 px-1">
            {pages.map((entry, index) =>
              entry === 'gap' ? (
                <li
                  key={`gap-${index}`}
                  aria-hidden
                  className="flex size-[var(--h-control-sm)] items-center justify-center text-muted"
                >
                  …
                </li>
              ) : (
                <li key={entry}>
                  <button
                    type="button"
                    disabled={disabled}
                    aria-label={`Page ${entry}`}
                    aria-current={entry === page ? 'page' : undefined}
                    onClick={() => onPageChange?.(entry)}
                    className={cn(
                      'flex h-[var(--h-control-sm)] min-w-[var(--h-control-sm)] items-center justify-center',
                      'rounded-[var(--radius-sm)] px-1.5 text-sm tnum',
                      'transition-colors ease-[var(--ease-out)] duration-[var(--dur-fast)]',
                      'disabled:pointer-events-none disabled:opacity-40',
                      'focus-visible:outline-2 focus-visible:outline-offset-1 outline-[color:var(--focus)]',
                      entry === page
                        ? 'bg-[var(--surface-selected)] font-medium text-primary'
                        : 'text-muted hover:bg-[var(--surface-hover)] hover:text-primary active:bg-[var(--surface-active)]',
                    )}
                  >
                    {entry}
                  </button>
                </li>
              ),
            )}
          </ol>
        ) : null}

        <NavButton
          label="Next page"
          icon={ChevronRight}
          disabled={!canNext}
          onClick={() => (mode === 'pages' ? onPageChange?.(page + 1) : onNext?.())}
        />

        {mode === 'pages' ? (
          <NavButton
            label="Last page"
            icon={ChevronsRight}
            disabled={!canNext}
            onClick={() => onPageChange?.(pageCount ?? 1)}
          />
        ) : null}
      </div>
    </nav>
  );
}

const pageSizeTriggerClass = cn(
  'flex h-[var(--h-control-sm)] shrink-0 items-center gap-1.5 rounded-[var(--radius-sm)] px-2',
  'text-sm text-primary transition-colors ease-[var(--ease-out)] duration-[var(--dur-fast)]',
  'hover:bg-[var(--surface-hover)] active:bg-[var(--surface-active)]',
  'data-[state=open]:bg-[var(--surface-hover)]',
  'disabled:pointer-events-none disabled:opacity-40',
  'focus-visible:outline-2 focus-visible:outline-offset-1 outline-[color:var(--focus)]',
);

function NavButton({
  label,
  icon: Icon,
  disabled,
  onClick,
}: {
  label: string;
  icon: typeof ChevronLeft;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <Tooltip content={label} disabled={disabled}>
      <button
        type="button"
        aria-label={label}
        disabled={disabled}
        onClick={onClick}
        className={cn(
          'flex size-[var(--h-control-sm)] shrink-0 items-center justify-center',
          'rounded-[var(--radius-sm)] text-muted transition-colors ease-[var(--ease-out)] duration-[var(--dur-fast)]',
          'hover:bg-[var(--surface-hover)] hover:text-primary',
          'active:bg-[var(--surface-active)]',
          'disabled:pointer-events-none disabled:opacity-35',
          'focus-visible:outline-2 focus-visible:outline-offset-1 outline-[color:var(--focus)]',
        )}
      >
        <Icon className="size-4" aria-hidden />
      </button>
    </Tooltip>
  );
}

function formatNumber(value: number): string {
  return value.toLocaleString('en-US');
}

/** `1 … 4 5 6 … 20` — first, last, and a window of three around the current page. */
export function buildPageList(page: number, pageCount: number): Array<number | 'gap'> {
  if (pageCount <= 7) return Array.from({ length: pageCount }, (_, i) => i + 1);

  const window = new Set<number>([1, pageCount, page, page - 1, page + 1]);
  if (page <= 3) [2, 3, 4].forEach((n) => window.add(n));
  if (page >= pageCount - 2) [pageCount - 3, pageCount - 2, pageCount - 1].forEach((n) => window.add(n));

  const sorted = [...window].filter((n) => n >= 1 && n <= pageCount).sort((a, b) => a - b);
  const out: Array<number | 'gap'> = [];
  let previous = 0;
  for (const n of sorted) {
    if (previous && n - previous > 1) out.push('gap');
    out.push(n);
    previous = n;
  }
  return out;
}
